/**
 * Article Master Template JavaScript
 * 
 * Handles:
 * 1. Content cleanup (removes empty br tags, nbsp, italic from H2)
 * 2. FAQ detection and transformation (GemPages accordion to clean FAQ)
 * 3. Table of Contents generation
 * 
 * All scripts run once DOM is ready for optimal performance
 */

(function() {
  'use strict';

  /**
   * ========================================
   * 1. CONTENT CLEANUP
   * ========================================
   * Removes empty br tags, nbsp spaces, and removes <i> tags from H2 headings
   */
  function cleanArticleContent() {
    // Try article master body first
    let articleBody = document.querySelector('.article-master-content__body');
    
    // Also clean digest sections (research points, takeaways, CTA, recipe) when present
    const digestSections = [
      '.article-master-content__research-points--digest',
      '.article-master-content__takeaways--digest',
      '.article-master-content__cta--digest',
      '.article-master-content__research-points',
      '.article-master-content__takeaways',
      '.article-master-content__cta',
      '.article-master-content__recipe',
      '.article-master-content__recipe-description',
      '.article-master-content__recipe-ingredients',
      '.article-master-content__recipe-instructions'
    ];
    
    digestSections.forEach(function(selector) {
      const section = document.querySelector(selector);
      if (section) {
        cleanSectionContent(section);
      }
    });
    
    // Hide empty recipe description divs
    const recipeDescriptions = document.querySelectorAll('.article-master-content__recipe-description');
    recipeDescriptions.forEach(function(desc) {
      // Check if element is empty or only contains whitespace/empty tags
      const textContent = desc.textContent || desc.innerText || '';
      const hasContent = textContent.trim().length > 0;
      
      if (!hasContent) {
        desc.style.display = 'none';
        desc.style.marginBottom = '0';
      }
    });
    
    // If not found, try research article page content
    if (!articleBody) {
      articleBody = document.querySelector('.research-article__page-content');
    }
    
    // If not found, try landing page container (to include all content)
    if (!articleBody) {
      articleBody = document.querySelector('.landing-page__container');
    }
    
    // If not found, try landing page content
    if (!articleBody) {
      articleBody = document.querySelector('.landing-page__page-content');
    }
    
    // If not found, try landing page text content
    if (!articleBody) {
      articleBody = document.querySelector('.landing-page__text-content');
    }
    
    if (!articleBody) return;
    
    // Remove <i> and <em> tags from H2 headings (unwrap the content)
    const h2Headings = articleBody.querySelectorAll('h2');
    h2Headings.forEach(function(h2) {
      const italicTags = h2.querySelectorAll('i, em');
      italicTags.forEach(function(italic) {
        // Replace the italic tag with its text content
        const parent = italic.parentNode;
        while (italic.firstChild) {
          parent.insertBefore(italic.firstChild, italic);
        }
        parent.removeChild(italic);
      });
    });
    
    // Remove all &nbsp; entities (both HTML entity and Unicode)
    // Use innerHTML replacement to handle HTML-encoded entities like <span>&nbsp;</span>
    // This catches cases where GemPages or other tools output HTML entities
    // Process all elements within the container, including GemPages content (.gp-text-instant)
    const allElements = articleBody.querySelectorAll('*');
    allElements.forEach(function(el) {
      if (el.innerHTML && el.innerHTML.indexOf('&nbsp;') !== -1) {
        el.innerHTML = el.innerHTML
          .replace(/&nbsp;/g, ' ')
          .replace(/<span>\s*<\/span>/g, '') // Remove empty spans
          .replace(/<span>(\s+)<\/span>/g, '$1'); // Unwrap spans with only whitespace
      }
    });
    
    // Also process the container itself
    if (articleBody.innerHTML && articleBody.innerHTML.indexOf('&nbsp;') !== -1) {
      articleBody.innerHTML = articleBody.innerHTML
        .replace(/&nbsp;/g, ' ')
        .replace(/<span>\s*<\/span>/g, '') // Remove empty spans
        .replace(/<span>(\s+)<\/span>/g, '$1'); // Unwrap spans with only whitespace
    }
    
    // Also use TreeWalker to handle Unicode non-breaking spaces in text nodes
    // This handles cases where entities were already parsed by the browser
    const walker = document.createTreeWalker(
      articleBody,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let textNode;
    while (textNode = walker.nextNode()) {
      // Replace Unicode non-breaking spaces
      if (textNode.textContent.indexOf('\u00A0') !== -1 || 
          textNode.textContent.indexOf('\u2007') !== -1 || 
          textNode.textContent.indexOf('\u202F') !== -1) {
        textNode.textContent = textNode.textContent
          .replace(/\u00A0/g, ' ')
          .replace(/\u2007/g, ' ') // Figure space
          .replace(/\u202F/g, ' '); // Narrow no-break space
      }
    }
    
    // Remove empty <br> tags
    const allBrTags = articleBody.querySelectorAll('br');
    allBrTags.forEach(function(br) {
      // Check if br is empty or only contains whitespace
      const nextSibling = br.nextSibling;
      const prevSibling = br.previousSibling;
      
      // Remove if it's a standalone br with no meaningful content around it
      if (!nextSibling || 
          (nextSibling.nodeType === Node.TEXT_NODE && !nextSibling.textContent.trim()) ||
          (nextSibling.nodeType === Node.ELEMENT_NODE && nextSibling.tagName === 'BR')) {
        // Check previous sibling too
        if (!prevSibling || 
            (prevSibling.nodeType === Node.TEXT_NODE && !prevSibling.textContent.trim()) ||
            (prevSibling.nodeType === Node.ELEMENT_NODE && prevSibling.tagName === 'BR')) {
          br.remove();
        }
      }
    });
    
    // Remove consecutive <br> tags (keep only one)
    let foundBr = true;
    while (foundBr) {
      foundBr = false;
      const brTags = articleBody.querySelectorAll('br');
      for (let i = 0; i < brTags.length - 1; i++) {
        const currentBr = brTags[i];
        const nextBr = currentBr.nextSibling;
        
        // Check if next sibling is also a br
        if (nextBr && nextBr.nodeType === Node.ELEMENT_NODE && nextBr.tagName === 'BR') {
          // Check if there's only whitespace between them
          const betweenNodes = [];
          let node = currentBr.nextSibling;
          while (node && node !== nextBr) {
            betweenNodes.push(node);
            node = node.nextSibling;
          }
          
          const hasOnlyWhitespace = betweenNodes.every(function(n) {
            return n.nodeType === Node.TEXT_NODE && !n.textContent.trim();
          });
          
          if (hasOnlyWhitespace || betweenNodes.length === 0) {
            nextBr.remove();
            foundBr = true;
            break;
          }
        }
      }
    }
    
    // Clean up empty paragraphs and divs that might contain only br or nbsp
    const emptyElements = articleBody.querySelectorAll('p, div');
    emptyElements.forEach(function(el) {
      const text = el.textContent || el.innerText || '';
      const hasOnlyWhitespace = !text.trim() || text.trim() === '' || text.trim() === '\u00A0';
      const hasOnlyBr = el.querySelectorAll('br').length > 0 && text.trim() === '';
      
      if (hasOnlyWhitespace || hasOnlyBr) {
        // Check if element has any meaningful attributes or children
        if (el.children.length === 0 && el.attributes.length <= 1) {
          el.remove();
        }
      }
    });
  }

  /**
   * Clean content in a specific section (for digest sections)
   */
  function cleanSectionContent(section) {
    if (!section) return;
    
    // Remove all &nbsp; entities
    const allElements = section.querySelectorAll('*');
    allElements.forEach(function(el) {
      if (el.innerHTML && el.innerHTML.indexOf('&nbsp;') !== -1) {
        el.innerHTML = el.innerHTML
          .replace(/&nbsp;/g, ' ')
          .replace(/<span>\s*<\/span>/g, '')
          .replace(/<span>(\s+)<\/span>/g, '$1');
      }
    });
    
    // Process the container itself
    if (section.innerHTML && section.innerHTML.indexOf('&nbsp;') !== -1) {
      section.innerHTML = section.innerHTML
        .replace(/&nbsp;/g, ' ')
        .replace(/<span>\s*<\/span>/g, '')
        .replace(/<span>(\s+)<\/span>/g, '$1');
    }
    
    // Handle Unicode non-breaking spaces in text nodes
    const walker = document.createTreeWalker(
      section,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let textNode;
    while (textNode = walker.nextNode()) {
      if (textNode.textContent.indexOf('\u00A0') !== -1 || 
          textNode.textContent.indexOf('\u2007') !== -1 || 
          textNode.textContent.indexOf('\u202F') !== -1) {
        textNode.textContent = textNode.textContent
          .replace(/\u00A0/g, ' ')
          .replace(/\u2007/g, ' ')
          .replace(/\u202F/g, ' ');
      }
    }
    
    // Remove empty <br> tags
    const allBrTags = section.querySelectorAll('br');
    allBrTags.forEach(function(br) {
      const nextSibling = br.nextSibling;
      const prevSibling = br.previousSibling;
      
      if (!nextSibling || 
          (nextSibling.nodeType === Node.TEXT_NODE && !nextSibling.textContent.trim()) ||
          (nextSibling.nodeType === Node.ELEMENT_NODE && nextSibling.tagName === 'BR')) {
        if (!prevSibling || 
            (prevSibling.nodeType === Node.TEXT_NODE && !prevSibling.textContent.trim()) ||
            (prevSibling.nodeType === Node.ELEMENT_NODE && prevSibling.tagName === 'BR')) {
          br.remove();
        }
      }
    });
    
    // Remove consecutive <br> tags
    let foundBr = true;
    while (foundBr) {
      foundBr = false;
      const brTags = section.querySelectorAll('br');
      for (let i = 0; i < brTags.length - 1; i++) {
        const currentBr = brTags[i];
        const nextBr = currentBr.nextSibling;
        
        if (nextBr && nextBr.nodeType === Node.ELEMENT_NODE && nextBr.tagName === 'BR') {
          const betweenNodes = [];
          let node = currentBr.nextSibling;
          while (node && node !== nextBr) {
            betweenNodes.push(node);
            node = node.nextSibling;
          }
          
          const hasOnlyWhitespace = betweenNodes.every(function(n) {
            return n.nodeType === Node.TEXT_NODE && !n.textContent.trim();
          });
          
          if (hasOnlyWhitespace || betweenNodes.length === 0) {
            nextBr.remove();
            foundBr = true;
            break;
          }
        }
      }
    }
    
    // Clean up empty paragraphs and divs
    const emptyElements = section.querySelectorAll('p, div');
    emptyElements.forEach(function(el) {
      const text = el.textContent || el.innerText || '';
      const hasOnlyWhitespace = !text.trim() || text.trim() === '' || text.trim() === '\u00A0';
      const hasOnlyBr = el.querySelectorAll('br').length > 0 && text.trim() === '';
      
      if (hasOnlyWhitespace || hasOnlyBr) {
        if (el.children.length === 0 && el.attributes.length <= 1) {
          el.remove();
        }
      }
    });
  }

  /**
   * ========================================
   * 2. FAQ DETECTION AND TRANSFORMATION
   * ========================================
   * Handles two patterns:
   * 1. Simple pattern: Heading + <p><strong>Q</strong></p> + <p>A</p> pairs
   * 2. GemPages accordion: Heading + <div data-accordion-group> with <div data-accordion> items
   */
  
  function initInlineFaq() {
    // Support Article Master, Research Article, and Landing Page containers
    var articleBody =
      document.querySelector('.article-master-content__body') ||
      document.querySelector('.research-article__page-content') ||
      document.querySelector('.research-article') ||
      document.querySelector('.landing-page__container') ||
      document.querySelector('.landing-page__page-content') ||
      document.querySelector('.landing-page__text-content');
      
    if (!articleBody) {
      console.log('[FAQ] No article body container found, skipping FAQ init');
      return;
    }
    console.log('[FAQ] initInlineFaq using container:', articleBody.className || articleBody.id || articleBody.tagName);

    // Find the FAQ heading (h2 or h3 containing "frequently asked questions" or "faq")
    var headingCandidates = articleBody.querySelectorAll('h1, h2, h3');
    var faqHeading = null;

    for (var i = 0; i < headingCandidates.length; i++) {
      var h = headingCandidates[i];
      var text = (h.textContent || '').toLowerCase();
      if (
        text.indexOf('frequently asked questions') !== -1 ||
        text.indexOf('faq') !== -1 ||
        text.indexOf('your questions') !== -1 // for headings like "Your Questions, Answered."
      ) {
        faqHeading = h;
        break;
      }
    }

    if (!faqHeading) {
      console.log('[FAQ] No FAQ heading found (looking for text containing "faq" or "your questions")');
      return;
    }
    console.log('[FAQ] Found FAQ heading:', (faqHeading.textContent || '').trim());

    // Check if there's a GemPages accordion structure nearby
    var accordionGroup = null;
    var currentNode = faqHeading.parentElement;
    var searchDepth = 0;
    while (currentNode && searchDepth < 5) {
      accordionGroup = currentNode.querySelector('[data-accordion-group], [data-accordion]');
      if (accordionGroup) break;
      currentNode = currentNode.parentElement;
      searchDepth++;
    }

    // If we found GemPages accordion, transform it
    if (accordionGroup) {
      console.log('[FAQ] Found GemPages accordion near heading, transforming to FAQ layout');
      transformGemPagesAccordion(faqHeading, accordionGroup);
    } else {
      console.log('[FAQ] No GemPages accordion found near heading, falling back to simple FAQ pattern');
      // Fallback to simple pattern detection
      transformSimpleFaq(faqHeading);
    }
  }

  function transformGemPagesAccordion(faqHeading, accordionGroup) {
    // Find the accordion group container - search more broadly
    var accordionContainer = accordionGroup.closest('[data-accordion-group]');
    if (!accordionContainer) {
      // If accordionGroup itself is the container, use it
      if (accordionGroup.hasAttribute && accordionGroup.hasAttribute('data-accordion-group')) {
        accordionContainer = accordionGroup;
      } else {
        // Try to find it by searching from the heading within known content containers
        var searchContainer =
          faqHeading.closest('.article-master-content__body') ||
          faqHeading.closest('.research-article__page-content') ||
          faqHeading.closest('.research-article') ||
          faqHeading.closest('.landing-page__container') ||
          faqHeading.closest('.landing-page__page-content') ||
          faqHeading.closest('.landing-page__text-content');
          
        if (searchContainer) {
          accordionContainer = searchContainer.querySelector('[data-accordion-group]');
        }

        // If still not found, fall back to a broader search on the page
        if (!accordionContainer) {
          accordionContainer =
            document.querySelector('.research-article [data-accordion-group]') ||
            document.querySelector('.article-master-content__body [data-accordion-group]') ||
            document.querySelector('.landing-page__container [data-accordion-group]') ||
            document.querySelector('[data-accordion-group]');
        }
      }
    }
    if (!accordionContainer) {
      console.log('[FAQ] Could not find accordion container starting from heading');
      return;
    }

    // Get ALL accordion items - including hidden ones
    var accordionItems = accordionContainer.querySelectorAll('[data-accordion]');
    if (!accordionItems.length) {
      console.log('[FAQ] Accordion container found but no [data-accordion] items inside');
      return;
    }
    console.log('[FAQ] Transforming', accordionItems.length, 'accordion items into FAQ items');

    // Build FAQ wrapper
    var parent = faqHeading.parentNode;
    if (!parent) return;

    var faqWrapper = document.createElement('div');
    faqWrapper.className = 'article-master-content__faq';

    var faqTitle = faqHeading;
    faqTitle.classList.add('article-master-content__faq-title');

    var faqContent = document.createElement('div');
    faqContent.className = 'article-master-content__faq-content';

    // Insert wrapper before the heading
    parent.insertBefore(faqWrapper, faqTitle);
    faqWrapper.appendChild(faqTitle);
    faqWrapper.appendChild(faqContent);

    var qaPairs = [];

    // Process each accordion item - including hidden ones
    for (var i = 0; i < accordionItems.length; i++) {
      var item = accordionItems[i];
      
      // Find question in data-control section - check multiple possible structures
      var controlDiv = item.querySelector('[data-control]');
      if (!controlDiv) {
        var textBlock = item.querySelector('.text-edit, .elm');
        controlDiv = textBlock || item;
      }
      
      // Look for question text - try multiple selectors
      var questionEl = null;
      var questionText = '';
      
      // First try h3 or h2 (common for FAQ questions in GemPages)
      questionEl = controlDiv ? controlDiv.querySelector('h3, h2') : null;
      if (questionEl) {
        questionText = (questionEl.textContent || '').trim();
      }
      
      // If no heading found, try p > strong
      if (!questionText) {
        var questionP = controlDiv ? controlDiv.querySelector('p strong, p > strong, strong') : null;
        if (questionP) {
          var questionParagraph = questionP.closest('p') || questionP.parentElement;
          questionText = (questionParagraph ? questionParagraph.textContent : questionP.textContent || '').trim();
          questionEl = questionParagraph || questionP;
        }
      }
      
      // If still no question, try to find any paragraph with strong text
      if (!questionText) {
        var allPs = controlDiv ? controlDiv.querySelectorAll('p') : [];
        for (var pIdx = 0; pIdx < allPs.length; pIdx++) {
          var strongEl = allPs[pIdx].querySelector('strong');
          if (strongEl) {
            questionText = (allPs[pIdx].textContent || '').trim();
            questionEl = allPs[pIdx];
            break;
          }
        }
      }
      
      // Last resort: get text from the control div itself
      if (!questionText) {
        var controlText = (controlDiv ? controlDiv.textContent : '').trim();
        if (controlText && controlText.length < 200) {
          questionText = controlText;
          questionEl = controlDiv;
        } else {
          continue;
        }
      }
      
      if (!questionText) continue;

      // Find answer in data-content section
      var contentDiv = item.querySelector('[data-content]');
      var answerTexts = [];
      if (contentDiv) {
        var answerPs = contentDiv.querySelectorAll('p');
        for (var j = 0; j < answerPs.length; j++) {
          var answerText = (answerPs[j].textContent || '').trim();
          if (answerText) {
            answerTexts.push(answerText);
          }
        }
      }
      
      // If no answer found in data-content, try to find it elsewhere
      if (answerTexts.length === 0) {
        var articleEl = item.querySelector('article');
        if (articleEl) {
          var articlePs = articleEl.querySelectorAll('p');
          for (var k = 0; k < articlePs.length; k++) {
            var artText = (articlePs[k].textContent || '').trim();
            if (artText && artText !== questionText) {
              answerTexts.push(artText);
            }
          }
        }

        if (answerTexts.length === 0 && controlDiv) {
          var controlPs = controlDiv.querySelectorAll('p');
          var questionParagraph = questionEl && questionEl.tagName === 'P' ? questionEl : null;
          for (var c = 0; c < controlPs.length; c++) {
            var pEl = controlPs[c];
            if (questionParagraph && pEl === questionParagraph) continue;
            var ctrlText = (pEl.textContent || '').trim();
            if (ctrlText && ctrlText !== questionText) {
              answerTexts.push(ctrlText);
            }
          }
        }
      }

      // Create FAQ item
      if (questionText) {
        var faqItem = document.createElement('div');
        faqItem.className = 'article-master-content__faq-item';

        var questionEl = document.createElement('p');
        questionEl.className = 'article-master-content__faq-question';
        var strongEl = document.createElement('strong');
        strongEl.textContent = questionText;
        questionEl.appendChild(strongEl);
        faqItem.appendChild(questionEl);

        if (answerTexts.length > 0) {
          for (var k = 0; k < answerTexts.length; k++) {
            var answerEl = document.createElement('p');
            answerEl.className = 'article-master-content__faq-answer';
            answerEl.textContent = answerTexts[k];
            faqItem.appendChild(answerEl);
          }
          
          qaPairs.push({
            question: questionText,
            answer: answerTexts.join('\n\n')
          });
        }

        faqContent.appendChild(faqItem);
      }
    }

    // Hide the original GemPages accordion structure
    if (accordionContainer.parentElement) {
      console.log('[FAQ] Hiding original GemPages accordion container after FAQ transform');
      accordionContainer.parentElement.style.display = 'none';
    }

    // Generate FAQPage JSON-LD
    if (qaPairs.length) {
      generateFaqSchema(qaPairs);
    }
  }

  function transformSimpleFaq(faqHeading) {
    var parent = faqHeading.parentNode;
    if (!parent) return;

    var faqWrapper = document.createElement('div');
    faqWrapper.className = 'article-master-content__faq';

    var faqTitle = faqHeading;
    faqTitle.classList.add('article-master-content__faq-title');

    var faqContent = document.createElement('div');
    faqContent.className = 'article-master-content__faq-content';

    parent.insertBefore(faqWrapper, faqTitle);
    faqWrapper.appendChild(faqTitle);
    faqWrapper.appendChild(faqContent);

    var stopTags = { 'H1': true, 'H2': true };
    var node = faqTitle.nextSibling;

    while (node) {
      var next = node.nextSibling;
      if (node.nodeType === Node.ELEMENT_NODE && stopTags[node.tagName]) {
        break;
      }
      faqContent.appendChild(node);
      node = next;
    }

    var qaPairs = [];
    var children = faqContent.children;

    function isQuestionParagraph(el) {
      if (!el || el.tagName !== 'P') return false;
      var strongs = el.querySelectorAll('strong');
      if (strongs.length !== 1) return false;
      var strongText = (strongs[0].textContent || '').trim();
      return !!strongText;
    }

    for (var j = 0; j < children.length; j++) {
      var child = children[j];
      if (isQuestionParagraph(child)) {
        var questionText = (child.textContent || '').trim();
        var answerParts = [];
        var k = j + 1;
        while (k < children.length && !isQuestionParagraph(children[k])) {
          if (children[k].tagName === 'P') {
            var answerText = (children[k].textContent || '').trim();
            if (answerText) answerParts.push(answerText);
          }
          k++;
        }
        if (questionText && answerParts.length) {
          qaPairs.push({
            question: questionText,
            answer: answerParts.join('\n\n')
          });
        }
      }
    }

    if (qaPairs.length) {
      generateFaqSchema(qaPairs);
    }
  }

  function generateFaqSchema(qaPairs) {
    try {
      var faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": qaPairs.map(function(qa) {
          return {
            "@type": "Question",
            "name": qa.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": qa.answer
            }
          };
        })
      };

      var schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.text = JSON.stringify(faqSchema);
      document.head.appendChild(schemaScript);
    } catch (e) {
      // Fail silently
    }
  }

  function makeFaqInteractive() {
    var faqItems = document.querySelectorAll('.article-master-content__faq-item');
    if (!faqItems.length) {
      console.log('[FAQ] No .article-master-content__faq-item elements found for interactivity');
      return;
    }
    console.log('[FAQ] Initializing interactive FAQ on', faqItems.length, 'items');
    
    faqItems.forEach(function(item) {
      var question = item.querySelector('.article-master-content__faq-question');
      var answers = item.querySelectorAll('.article-master-content__faq-answer');
      
      if (!question) return;
      
      // Make question clickable
      question.style.cursor = 'pointer';
      question.setAttribute('role', 'button');
      question.setAttribute('aria-expanded', 'false');
      question.setAttribute('tabindex', '0');
      
      // Add accordion icon if not already present
      var existingIcon = question.querySelector('.article-master-content__faq-icon');
      if (!existingIcon) {
        var icon = document.createElement('span');
        icon.className = 'article-master-content__faq-icon';
        icon.innerHTML = '+';
        question.appendChild(icon);
      }
      
      // Hide answers by default
      answers.forEach(function(answer) {
        answer.style.display = 'none';
      });
      
      // If no answers, disable the question
      if (!answers.length) {
        question.style.opacity = '0.6';
        question.style.cursor = 'default';
        return;
      }
      
      // Toggle on click (single-open accordion)
      function toggleAnswer() {
        var isExpanded = question.getAttribute('aria-expanded') === 'true';
        var newState = !isExpanded;

        // First close all other FAQ items
        var allItems = document.querySelectorAll('.article-master-content__faq-item');
        allItems.forEach(function(otherItem) {
          var otherQuestion = otherItem.querySelector('.article-master-content__faq-question');
          var otherAnswers = otherItem.querySelectorAll('.article-master-content__faq-answer');
          if (!otherQuestion || otherItem === item) return;

          otherQuestion.setAttribute('aria-expanded', 'false');
          var otherIcon = otherQuestion.querySelector('.article-master-content__faq-icon');
          if (otherIcon) {
            otherIcon.innerHTML = '+';
          }
          otherItem.classList.remove('is-open');

          otherAnswers.forEach(function(ans) {
            ans.style.display = 'none';
          });
        });

        // Then toggle the clicked one
        question.setAttribute('aria-expanded', newState ? 'true' : 'false');
        var icon = question.querySelector('.article-master-content__faq-icon');
        if (icon) {
          icon.innerHTML = newState ? '−' : '+';
        }
        if (newState) {
          item.classList.add('is-open');
        } else {
          item.classList.remove('is-open');
        }

        answers.forEach(function(answer) {
          answer.style.display = newState ? 'block' : 'none';
        });
        
        // Scroll to show the full accordion when opening
        if (newState) {
          setTimeout(function() {
            var questionRect = question.getBoundingClientRect();
            var scrollOffset = 120;
            var currentScroll = window.pageYOffset || document.documentElement.scrollTop;
            var questionTop = questionRect.top + currentScroll;
            var targetScroll = questionTop - scrollOffset;
            
            window.scrollTo({
              top: Math.max(0, targetScroll),
              behavior: 'smooth'
            });
          }, 150);
        }
      }
      
      question.addEventListener('click', toggleAnswer);
      
      // Keyboard support
      question.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleAnswer();
        }
      });
    });
  }

  function scheduleFaqInit() {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(function() {
        initInlineFaq();
        setTimeout(makeFaqInteractive, 100);
      }, { timeout: 2000 });
    } else {
      setTimeout(function() {
        initInlineFaq();
        setTimeout(makeFaqInteractive, 100);
      }, 800);
    }
  }

  /**
   * ========================================
   * 3. TABLE OF CONTENTS GENERATION
   * ========================================
   */
  function generateTOC() {
    // Cache selectors for better performance
    const tocList = document.querySelector('.article-master-content__toc-list');
    const contentBody = document.querySelector('.article-master-content__body');
    
    if (!tocList || !contentBody) return;
    
    // Cache header element and offset
    let headerElement = document.querySelector('.page__header, .header, [class*="header"]');
    let headerOffset = 80;
    if (headerElement) {
      headerOffset = headerElement.getBoundingClientRect().height + 20;
    }
    
    // Find all H2 headings in the article content
    const headings = Array.from(contentBody.querySelectorAll('h2'));
    
    // Clear existing TOC items
    tocList.innerHTML = '';
    
    if (headings.length === 0) return;
    
    // Batch DOM operations - create document fragment
    const fragment = document.createDocumentFragment();
    
    // Generate TOC items
    headings.forEach((heading, index) => {
      if (!heading.textContent || !heading.textContent.trim()) return;
      
      // Create ID for heading if it doesn't have one
      let headingId = heading.id;
      if (!headingId) {
        headingId = 'heading-' + index + '-' + heading.textContent.trim().toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        heading.id = headingId;
      }
      
      // Create list item and link
      const listItem = document.createElement('li');
      const link = document.createElement('a');
      link.href = '#' + headingId;
      link.textContent = heading.textContent.trim();
      
      // Use passive event listener for better performance
      link.addEventListener('click', function(e) {
        e.preventDefault();
        const headingTop = heading.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: headingTop - headerOffset, behavior: 'smooth' });
        window.history.pushState(null, null, '#' + headingId);
      }, { passive: false });
      
      listItem.appendChild(link);
      fragment.appendChild(listItem);
    });
    
    // Append all items at once (single reflow)
    tocList.appendChild(fragment);
  }

  /**
   * ========================================
   * INITIALIZATION
   * ========================================
   * Run all functions when DOM is ready
   */
  function init() {
    // Only run if we're on a page with the content section (article master, research article, or landing page)
    const hasContent = document.querySelector('.article-master-content__body') ||
                       document.querySelector('.research-article__page-content') ||
                       document.querySelector('.landing-page__container') ||
                       document.querySelector('.landing-page__page-content') ||
                       document.querySelector('.landing-page__text-content');
    if (!hasContent) return;

    // 1. Run content cleanup once DOM is ready
    function runCleanup() {
      cleanArticleContent();
      
      // For GemPages content that loads dynamically, run cleanup again after a delay
      // This handles cases where content is loaded via AJAX or after initial page load
      setTimeout(function() {
        cleanArticleContent();
      }, 1000);
      
      // Also use MutationObserver to watch for dynamically added content (like GemPages)
      if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(function(mutations) {
          let shouldClean = false;
          mutations.forEach(function(mutation) {
            if (mutation.addedNodes.length > 0) {
              // Check if any added nodes contain GemPages content or our target classes
              mutation.addedNodes.forEach(function(node) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  if (node.classList && (
                    node.classList.contains('gp-text-instant') ||
                    node.classList.contains('landing-page__page-content') ||
                    node.classList.contains('landing-page__text-content') ||
                    node.querySelector('.gp-text-instant') ||
                    node.querySelector('.landing-page__page-content') ||
                    node.querySelector('.landing-page__text-content')
                  )) {
                    shouldClean = true;
                  }
                }
              });
            }
          });
          
          if (shouldClean) {
            // Debounce cleanup to avoid running too frequently
            setTimeout(cleanArticleContent, 100);
          }
        });
        
        // Observe the document body for changes
        const targetNode = document.body || document.documentElement;
        if (targetNode) {
          observer.observe(targetNode, {
            childList: true,
            subtree: true
          });
        }
      }
    }
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runCleanup, { once: true });
    } else {
      runCleanup();
    }

    // 2. Initialize FAQ (runs on idle to avoid blocking)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleFaqInit, { once: true });
    } else {
      scheduleFaqInit();
    }

    // 3. Generate TOC if it exists (non-critical, use idle callback)
    const tocList = document.querySelector('.article-master-content__toc-list');
    if (tocList) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
          if ('requestIdleCallback' in window) {
            requestIdleCallback(generateTOC, { timeout: 1000 });
          } else {
            setTimeout(generateTOC, 0);
          }
        }, { once: true });
      } else {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(generateTOC, { timeout: 1000 });
        } else {
          setTimeout(generateTOC, 0);
        }
      }
    }
  }

  // Initialize when script loads
  init();
})();

